import { NgStyle } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  ViewChild,
  afterRenderEffect,
  effect,
  input,
  output
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { fitIntoViewport } from '@shared/utils/fit-into-viewport';

import { ContextMenuSubmenuComponent } from './context-menu-submenu.component';
import { SubmenuState } from './submenu-state';

export interface ContextMenuOption {
  label: string;
  action: () => void;
  icon?: string;
  disabled?: boolean;
  /** A second, quieter line under the label: why a disabled entry is disabled. */
  hint?: string;
  /** Quieter text at the entry's right edge, describing what the label names. */
  meta?: string;
  isDanger?: boolean;
  subOptions?: ContextMenuOption[];
}

@Component({
  selector: 'app-context-menu',
  imports: [TranslatePipe, NgStyle, ContextMenuSubmenuComponent],
  templateUrl: './context-menu.component.html'
})
export class ContextMenuComponent implements OnDestroy {
  x = input<number>(0);
  y = input<number>(0);
  isVisible = input<boolean>(false);
  options = input<ContextMenuOption[]>([]);
  headerEntityType = input<string | undefined>(undefined);
  headerEntityName = input<string | undefined>(undefined);
  closeMenu = output<void>();

  @ViewChild('contextMenuElement', { static: false }) contextMenuElement?: ElementRef;

  private submenuState = new SubmenuState();

  readonly openSubMenuIndex = this.submenuState.openIndex;

  constructor() {
    effect(() => {
      if (!this.isVisible()) {
        this.submenuState.reset();
      }
    });
    // Opened at the pointer, the menu would leave the viewport near its bottom or right edge.
    afterRenderEffect(() => {
      this.x();
      this.y();
      this.options();
      if (this.isVisible() && this.contextMenuElement) {
        fitIntoViewport(this.contextMenuElement.nativeElement);
      }
    });
  }

  @HostListener('document:keydown', ['$event'])
  handleKeydown(event: KeyboardEvent) {
    switch (event.key) {
      case 'Escape':
        this.closeMenu.emit();
        break;
    }
  }

  ngOnDestroy(): void {
    this.submenuState.reset();
  }

  isAnyIconToDisplay(): boolean {
    return this.options().some((elt) => elt.icon);
  }

  hasSubOptions(option: ContextMenuOption): boolean {
    return !!option.subOptions && option.subOptions.length > 0;
  }

  showSubMenu(index: number, option: ContextMenuOption): void {
    this.submenuState.hoverEnter(index, this.hasSubOptions(option));
  }

  hideSubMenu(): void {
    this.submenuState.hoverLeave();
  }

  onOptionClick(option: ContextMenuOption): void {
    if (option.disabled) {
      return;
    }

    if (this.hasSubOptions(option)) {
      this.submenuState.togglePin(this.options().indexOf(option));
      return;
    }

    if (option.action) {
      option.action();
    }

    this.closeMenu.emit();
  }

  protected trackMenuOptionByLabelAndIndex(
    index: number,
    option: ContextMenuOption
  ): string {
    return `${option.label}_${index}`;
  }

  protected itemClasses(option: ContextMenuOption): string {
    if (option.disabled) {
      return 'cursor-not-allowed text-text-tertiary';
    }
    if (option.isDanger) {
      return 'cursor-pointer text-danger hover:bg-danger-soft hover:text-danger-hover';
    }
    return 'cursor-pointer text-text-primary hover:bg-interactive';
  }

  protected iconClasses(option: ContextMenuOption): string {
    if (option.disabled) return 'text-text-tertiary';
    if (option.isDanger) return 'text-danger';
    return 'text-text-secondary';
  }
}
